/**
 * What: Loads PostHog Conversations in the merchant's browser so support submissions create tickets in PostHog Support.
 * Transport / data-flow: posthog-js talks directly to PostHog's ingestion API from the browser; no TestFeed backend
 * involved in ticket creation. The project token, host, and (optional) identity hash come from an authenticated
 * Worker endpoint rather than being hardcoded, so they can be rotated per environment without a code change.
 *
 */
import posthog from 'posthog-js'
import { createPosthogSpaConfig } from '@testfeed/observability/posthog-spa'
import { MerchantFacingError } from './merchantErrors'
import { shopifyApiFetch, type ShopifyAppBridge } from './shopifyApiFetch'

const AVAILABILITY_POLL_MS = 200
const AVAILABILITY_TIMEOUT_MS = 5000

type SupportConfigResponse = {
  distinctId: string
  identityHash: string | null
  posthogToken: string
  posthogHost: string
}

let configPromise: Promise<SupportConfigResponse> | null = null
let initialized = false

function fetchSupportConfig(shopify: ShopifyAppBridge): Promise<SupportConfigResponse> {
  if (!configPromise) {
    configPromise = shopifyApiFetch(shopify, '/api/support-config')
      .then((response) => response.json() as Promise<SupportConfigResponse>)
      .catch((error: unknown) => {
        configPromise = null
        throw error
      })
  }
  return configPromise
}

/** Initializes posthog-js and ties identity once; retries on a later call if this attempt threw. */
async function ensureInitialized(shopify: ShopifyAppBridge) {
  if (initialized) return
  const config = await fetchSupportConfig(shopify)
  posthog.init(config.posthogToken, {
    ...createPosthogSpaConfig({
      token: config.posthogToken,
      apiHost: config.posthogHost,
      capturePageview: false,
      capturePageleave: false,
      captureWebVitals: false,
    }),
    // This SDK instance exists only to drive support tickets — keep it from picking up
    // whatever analytics defaults (autocapture, session replay, surveys) the project has set.
    autocapture: false,
    disable_session_recording: true,
    disable_surveys: true,
  })
  // We drive tickets from our own form; suppress PostHog's floating chat button. Conversations
  // loads lazily, so an early hide() call can no-op before the extension exists — re-issue it
  // once loading is confirmed, without blocking the caller on that confirmation.
  posthog.conversations.hide()
  void waitForConversationsAvailable().then((available) => {
    if (available) posthog.conversations.hide()
  })
  if (config.identityHash) posthog.setIdentity(config.distinctId, config.identityHash)
  initialized = true
}

function waitForConversationsAvailable(): Promise<boolean> {
  return new Promise((resolve) => {
    const deadline = Date.now() + AVAILABILITY_TIMEOUT_MS
    const check = () => {
      if (posthog.conversations.isAvailable()) {
        resolve(true)
        return
      }
      if (Date.now() >= deadline) {
        resolve(false)
        return
      }
      setTimeout(check, AVAILABILITY_POLL_MS)
    }
    check()
  })
}

export type SupportTicketTraits = { name?: string; email?: string }

/** Submits a merchant support message as a new PostHog Support ticket. */
export async function submitSupportTicket(shopify: ShopifyAppBridge, message: string, traits: SupportTicketTraits) {
  await ensureInitialized(shopify)
  const available = await waitForConversationsAvailable()
  if (!available) {
    const reason = posthog.conversations.getUnavailableReason() ?? 'unknown reason'
    throw new MerchantFacingError(`Support is unavailable right now (${reason}).`)
  }
  const response = await posthog.conversations.sendMessage(message, traits, true)
  // Sending a message can prompt the widget to render/open itself; keep it suppressed.
  posthog.conversations.hide()
  if (!response?.ticket_id) throw new MerchantFacingError('Failed to send support request.')
  return { supportRequestId: response.ticket_id }
}
